import {
  createBusinessEntityDocument,
  useBusinessEntity,
  useGetDriveView,
  useSupportedDocuments,
  useJurisdictions,
} from '@commenda/api';
import {
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeaturedIcon,
  FieldWrapper,
  FlagComponent,
  Icons,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
} from '@commenda/kakashi';
import { IDocument } from '@commenda/types/src/drive-view/types';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  stripParenthetical,
  buildFYOptions,
  DriveViewHelper,
  getLastNYears,
  MonthIndexOfMonth,
} from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import { Country as CountryPkg } from 'country-state-city';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useNotification } from '../../hooks/useNotification';

const documentSchema = z.object({
  documentType: z.nativeEnum(LegacyDocumentType, {
    errorMap: () => ({ message: 'Please select a document type' }),
  }),
  file: z
    .object({
      fileName: z.string().min(1, 'File name is required'),
      fileUrl: z.string().min(1, 'File URL is required'),
      documentType: z.nativeEnum(LegacyDocumentType, {
        errorMap: () => ({ message: 'Please select a document type' }),
      }),
    })
    .nullable(),
  financialYear: z.string().optional(),
});

type DocumentFormFields = z.infer<typeof documentSchema>;

const getJurisdictionDisplayName = (
  countryCode: string,
  jurisdictionCode: string | null | undefined,
  labelOf: (jurisdictionId?: string | null) => string
): string => {
  // The jurisdiction is the more specific answer where the entity has one.
  if (jurisdictionCode) return labelOf(jurisdictionCode);

  const country = CountryPkg.getCountryByCode(countryCode);
  return country?.name || countryCode;
};

export const AddDocumentModal = ({
  isOpen,
  onClose,
  businessEntityId,
  onSuccess,
  restrictTo,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  onSuccess: (activeFolderId: string, activeFolderName: string) => void;
  // When provided, the document-type picker is filtered to this set. The
  // "Other" fallback is also dropped since it'd let the user escape the
  // restriction. Use for context-scoped uploads (e.g. ownership documents).
  restrictTo?: LegacyDocumentType[];
}) => {
  const [modalState, setModalState] = useState<
    'UPLOAD_FILE' | 'UPLOAD_SUCCESS'
  >('UPLOAD_FILE');

  const { data: businessEntityResponse, error: businessEntityError } =
    useBusinessEntity(businessEntityId);

  const { data: supportedDocumentsResponse, error: supportedDocumentsError } =
    useSupportedDocuments({
      businessEntityId,
    });

  const { mutate: mutateDriveView } = useGetDriveView(
    businessEntityId,
    'new'
  );
  const { labelOf } = useJurisdictions();

  const [filePath, setFilePath] = useState<string | null>(null);
  const [activeFolderId, setActiveFolderId] = useState<string | null>('root');
  const [activeFolderName, setActiveFolderName] = useState<string | null>(
    'Documents'
  );

  const { setNotification } = useNotification();

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<DocumentFormFields>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      documentType: undefined,
      file: undefined,
      financialYear: undefined,
    },
  });

  const documentType = watch('documentType');
  const file = watch('file');

  const onCloseModal = () => {
    clearErrors();
    reset();
    setModalState('UPLOAD_FILE');
    setFilePath(null);
    onClose();
  };

  if (!supportedDocumentsResponse || !businessEntityResponse) {
    if (supportedDocumentsError) console.error(supportedDocumentsError);
    if (businessEntityError) console.error(businessEntityError);

    return (
      <Dialog open={isOpen} onOpenChange={onCloseModal}>
        <DialogContent preventCloseOnOutsideClick>
          <DialogHeader>
            <DialogTitle>Add new document</DialogTitle>
          </DialogHeader>
          <div className="p-3xl flex justify-center items-center min-h-[240px]">
            <LoadingIndicator size={48} />
          </div>
          <DialogFooter>
            <Button size="sm" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" disabled>
              Add document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const restrictSet = restrictTo ? new Set(restrictTo) : null;
  const documentTypeOptions = [
    ...supportedDocumentsResponse.supportedDocumentTypes.map(
      (documentRule) => ({
        value: documentRule.documentSubType,
        label: stripParenthetical(documentRule.documentName),
      })
    ),
    ...(restrictSet ? [] : [{ value: LegacyDocumentType.Other, label: 'Other' }]),
  ].filter((opt) => !restrictSet || restrictSet.has(opt.value));

  const hasNoDocumentTypes = documentTypeOptions.length === 0;

  const shouldShowFinancialYear =
    !!supportedDocumentsResponse.supportedDocumentTypes.find(
      (documentRule) => documentRule.documentSubType === documentType
    )?.isTimeSensitive;

  const financialYearOptions = businessEntityResponse.accountingInfo.fiscalYear
    ?.data?.FIXED_DATE
    ? buildFYOptions(
        businessEntityResponse.accountingInfo.fiscalYear?.data?.FIXED_DATE?.day,
        MonthIndexOfMonth[
          businessEntityResponse.accountingInfo.fiscalYear?.data?.FIXED_DATE
            ?.month
        ]
      )
    : [];

  const effectiveStartDate = shouldShowFinancialYear
    ? financialYearOptions.find((each) => each.value === watch('financialYear'))
        ?.period?.startDate
    : undefined;
  const effectiveEndDate = shouldShowFinancialYear
    ? financialYearOptions.find((each) => each.value === watch('financialYear'))
        ?.period?.endDate
    : undefined;

  const onSubmit = async (data: DocumentFormFields) => {
    try {
      const documentRule =
        supportedDocumentsResponse.supportedDocumentTypes.find(
          (rule) => rule.documentSubType === data.documentType
        );

      if (documentRule?.isTimeSensitive) {
        if (!data.financialYear) {
          setError('financialYear', {
            message: 'Please select a financial year',
          });
          return;
        }
      }

      const response = await createBusinessEntityDocument(businessEntityId, {
        documentSubType: data.documentType,
        fileUrl: data.file.fileUrl,
        fileName: data.file.fileName,
        effectiveStartDate,
        effectiveEndDate,
      });

      if (!response.ok) {
        if (response.status === 409) {
          setNotification({
            title: 'Error',
            message: 'Document already exists!',
            show: true,
            valid: false,
          });
          return;
        }
        setNotification({
          title: 'Error',
          message: 'Failed to add document',
          show: true,
          valid: false,
        });

        return;
      }

      const createdFile = (await response.json()) as { document: IDocument };
      const createdFileId = createdFile.document.id;

      let driveView = await mutateDriveView();
      let flPath = DriveViewHelper.getFilePathInfo(driveView, createdFileId);

      if (!flPath) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        driveView = await mutateDriveView();
        flPath = DriveViewHelper.getFilePathInfo(driveView, createdFileId);
      }

      if (flPath) {
        setFilePath(flPath.path);
        setActiveFolderId(flPath.folderIds.at(-1) ?? 'root');
        setActiveFolderName(flPath.folderNames.at(-1) ?? 'Documents');
      } else {
        setFilePath('Documents');
        setActiveFolderId('root');
        setActiveFolderName('Documents');
      }

      setModalState('UPLOAD_SUCCESS');
    } catch (error) {
      setNotification({
        title: 'Error',
        message: 'Failed to add document',
        show: true,
        valid: false,
      });
    }
  };

  const onGoToFile = () => {
    onSuccess(activeFolderId, activeFolderName);

    onCloseModal();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onCloseModal}>
      <DialogContent preventCloseOnOutsideClick>
        {modalState === 'UPLOAD_FILE' ? (
          hasNoDocumentTypes ? (
            <>
              <DialogHeader>
                <DialogTitle>Add new document</DialogTitle>
              </DialogHeader>
              <div className="p-3xl flex flex-col items-center gap-2xl min-h-[240px] justify-center">
                <FeaturedIcon icon={<Icons.AlertCircle />} variant="warning" />
                <div className="space-y-xs text-center">
                  <p className="txt-large-semibold text-text-primary">
                    No document types available
                  </p>
                  <p className="txt-medium-regular text-text-secondary">
                    Document types have not been configured for{' '}
                    {getJurisdictionDisplayName(
                      businessEntityResponse.generalCorporationInfo
                        .incorporationCountry,
                      businessEntityResponse.generalCorporationInfo
                        .jurisdiction,
                      labelOf
                    )}{' '}
                    entities yet. Please contact support for assistance.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  size="sm"
                  variant="secondary"
                  type="button"
                  onClick={onCloseModal}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit, (error) => {
                console.log('error', error);
              })}
            >
              <DialogHeader>
                <DialogTitle>Add new document</DialogTitle>
              </DialogHeader>
              <div className="p-3xl flex flex-col gap-2xl">
                <FieldWrapper>
                  <Label>Select document type</Label>
                  <Select
                    value={documentType}
                    onValueChange={(value: LegacyDocumentType) => {
                      clearErrors('documentType');
                      setValue('documentType', value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypeOptions.map((each) => (
                        <SelectItem key={each.value} value={each.value}>
                          {each.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.documentType && (
                    <InputErrorMessage message={errors.documentType.message} />
                  )}
                </FieldWrapper>
                {shouldShowFinancialYear ? (
                  <FieldWrapper>
                    <Label>Select financial year</Label>
                    <Select
                      value={watch('financialYear')}
                      onValueChange={(value: string) => {
                        clearErrors('financialYear');
                        setValue('financialYear', value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select financial year" />
                      </SelectTrigger>
                      <SelectContent>
                        {getLastNYears(4).map((each) => (
                          <SelectItem key={each} value={each.toString()}>
                            {each.toString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.financialYear && (
                      <InputErrorMessage
                        message={errors.financialYear.message}
                      />
                    )}
                  </FieldWrapper>
                ) : null}
                <FieldWrapper>
                  <Label>Attach file</Label>
                  <SingleFileUploader
                    companyId={
                      businessEntityResponse.generalCorporationInfo?.companyId
                    }
                    file={
                      file
                        ? {
                            fileName: file.fileName,
                            fileUrl: file.fileUrl,
                            documentType,
                          }
                        : undefined
                    }
                    setFile={(newFile: FileBasic) => {
                      if (newFile) {
                        clearErrors('file');
                        setValue('file', {
                          fileName: newFile.fileName,
                          fileUrl: newFile.fileUrl,
                          documentType: newFile.documentType,
                        });
                      }
                    }}
                    disabled={!documentType}
                  />
                  {errors.file && (
                    <InputErrorMessage message={errors.file.message} />
                  )}
                </FieldWrapper>
              </div>
              <DialogFooter>
                <Button
                  size="sm"
                  variant="secondary"
                  type="button"
                  onClick={onCloseModal}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <LoadingIndicator size={16} /> : null}
                  <span>Add document</span>
                </Button>
              </DialogFooter>
            </form>
          )
        ) : (
          <>
            <div className="flex flex-col gap-xl p-xl">
              <FeaturedIcon icon={<Icons.CheckCircle />} variant="success" />
              <div className="space-y-xs">
                <div className="flex items-center gap-md">
                  <span className="txt-large-bold">Document uploaded</span>
                  <span className="size-1 bg-fg-tertiary rounded-full" />
                  <div className="flex items-center gap-md">
                    <FlagComponent
                      country={
                        businessEntityResponse.generalCorporationInfo
                          .incorporationCountry as CountryCode
                      }
                      countryName={
                        businessEntityResponse.generalCorporationInfo
                          .incorporationCountry
                      }
                      _className="size-4"
                    />
                    <span className="txt-medium-regular text-text-tertiary">
                      {businessEntityResponse.generalCorporationInfo.legalName}
                    </span>
                  </div>
                </div>
                <p className="txt-small-regular text-text-secondary">
                  Your document has been added to: <br />
                  <span className="txt-small-bold">{filePath}</span>
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={onCloseModal}
              >
                Close
              </Button>
              <Button
                size="sm"
                variant="primary"
                type="button"
                onClick={onGoToFile}
              >
                <span>Go to file</span>
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
